import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { normalizeSearchText } from '@/lib/utils/searchUtils'; // A-quattuor.6
import { Textarea } from '@/components/ui/textarea';
import { UserAvatar } from '@/components/ui/user-avatar';
import { useCompanyUsers } from '@/hooks/useCompanyUsers';
import { cn } from '@/lib/utils';

interface MentionTextareaProps {
  value: string;
  onChange: (value: string, mentions: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  rows?: number;
}

export const MentionTextarea = ({
  value,
  onChange,
  placeholder,
  disabled,
  rows = 3
}: MentionTextareaProps) => {
  const { users } = useCompanyUsers();
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionPosition, setMentionPosition] = useState<{ top: number; left: number } | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Filtrar usuários baseado no texto após "@"
  // A-quattuor.6: busca sem acento/caixa/pontuação nos dois lados
  const filteredUsers = users.filter(user =>
    normalizeSearchText(user.full_name).includes(normalizeSearchText(mentionSearch))
  );

  // Detectar "@" e mostrar lista
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const cursorPosition = e.target.selectionStart;

    // Extrair menções do texto
    const mentions = extractMentions(newValue);
    onChange(newValue, mentions);

    // Verificar se há "@" antes do cursor
    const textBeforeCursor = newValue.substring(0, cursorPosition);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
      
      // Verificar se não há espaço após "@" (ainda está digitando o nome)
      if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
        setMentionSearch(textAfterAt);
        setShowMentions(true);
        setSelectedIndex(0);

        // Calcular posição do popover
        if (textareaRef.current) {
          const textarea = textareaRef.current;
          const rect = textarea.getBoundingClientRect();
          
          // Posicionar abaixo do textarea
          setMentionPosition({
            top: rect.bottom + 4,
            left: rect.left
          });
        }
        return;
      }
    }

    setShowMentions(false);
  };

  // Extrair user_ids das menções no formato @[Nome](user_id)
  const extractMentions = (text: string): string[] => {
    const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
    const mentions: string[] = [];
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
      mentions.push(match[2]); // user_id
    }

    return mentions;
  };

  // Inserir menção no texto
  const insertMention = (user: { id: string; full_name: string }) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const cursorPosition = textarea.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPosition);
    const textAfterCursor = value.substring(cursorPosition);

    // Encontrar início do "@"
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');
    const textBeforeAt = value.substring(0, lastAtIndex);
    
    // Criar menção no formato @[Nome](user_id)
    const mention = `@[${user.full_name}](${user.id})`;
    const newValue = textBeforeAt + mention + ' ' + textAfterCursor;

    const mentions = extractMentions(newValue);
    onChange(newValue, mentions);

    setShowMentions(false);
    setMentionSearch('');

    // Mover cursor após a menção
    setTimeout(() => {
      const newPosition = textBeforeAt.length + mention.length + 1;
      textarea.focus();
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  // Navegação por teclado
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showMentions || filteredUsers.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredUsers.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : filteredUsers.length - 1
        );
        break;
      case 'Enter':
        if (showMentions) {
          e.preventDefault();
          insertMention(filteredUsers[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowMentions(false);
        break;
    }
  };

  // Fechar ao clicar fora
  useEffect(() => {
    const handleClickOutside = () => setShowMentions(false);
    if (showMentions) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showMentions]);

  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        rows={rows}
        className="resize-none"
      />

      {/* Dropdown de menções */}
      {showMentions && filteredUsers.length > 0 && mentionPosition && (
        <div
          className="fixed z-50 w-64 bg-popover border rounded-md shadow-lg p-2 max-h-48 overflow-y-auto"
          style={{
            top: `${mentionPosition.top}px`,
            left: `${mentionPosition.left}px`
          }}
        >
          <div className="space-y-1">
            {filteredUsers.map((user, index) => (
              <button
                key={user.id}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  insertMention(user);
                }}
                className={cn(
                  "w-full flex items-center gap-2 p-2 rounded text-left transition-colors",
                  "hover:bg-accent",
                  selectedIndex === index && "bg-accent"
                )}
              >
                <UserAvatar
                  avatarUrl={user.avatar_url}
                  name={user.full_name}
                  size="sm"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {user.full_name}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
